/**
 * SOLID Review
 * S — Strong. One concern: the payment-success screen's retry timing — ask for the balance after a head start, retry a few times
 *     while Stripe's webhook lands, say what was granted.
 * O — Good. The head start, the attempts and their spacing are the only variation points, constants.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. Two inputs, one answer.
 * D — Good. One use case, the request contexts, the timers it is handed.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions, type IConfigReaction } from './Reactions';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';
import type { ClientRequests } from '../client/ClientRequests';
import type { IServiceViewTimers, TViewTimer } from './platform/IConfigDepViewPlatform';

const INITIAL_DELAY_MS = 2_000; // webhook may still be in-flight; give it a head-start
const MAX_ATTEMPTS = 4;
const RETRY_DELAY_MS = 3_000;
const READ_BALANCE: TClientUseCaseInput<'readBalance'> = {};

export interface IStateUsePaymentBalancePoll {
  readonly creditsGranted: number | null;
}

const NOTHING_YET: IStateUsePaymentBalancePoll = { creditsGranted: null };

/** What the poll reacts to: whether the tab is signed in, and as whom. */
export type TPollInputs = readonly [registered: boolean, playerId: string | null];

/**
 * THE BALANCE POLL (frontend-framework-oo P2, 2026-10-29, §28.294) — `hooks/usePaymentBalancePoll.ts`'s effect, now a reaction to
 * the screen's inputs: once the tab is signed in with a player id, a first read after 2 s, then up to four attempts 3 s apart; the
 * first balance read is KEPT by the Application (`readBalance` → the `balanceRead` decision) and shown here. After four attempts it
 * gives up silently; the pushed balance (the account watch) still brings the next change.
 *
 * The read happens once per controller — a field, as the hook's ref was: the hook's original kept it as state in its own
 * dependencies, and the effect cancelled its own first timer (the 2026 note in the hook). StrictMode's remount therefore reads
 * nothing more, as it did.
 */
export class PaymentBalancePoller implements IServiceViewModel<IStateUsePaymentBalancePoll> {
  private readonly state = new ObservableState<IStateUsePaymentBalancePoll>(NOTHING_YET);
  private fetched = false;
  private readonly declared: ReadonlyArray<IConfigReaction<TPollInputs>> = [{
    deps: (inputs) => inputs,
    run: ([registered, playerId]) => {
      if (!registered || !playerId || this.fetched) return undefined;
      this.fetched = true;
      let attempts = 0;
      let retryTimer: TViewTimer;
      const fetchBalance = async (): Promise<void> => {
        const answer = await this.useCases.readBalance(this.requests.context(), READ_BALANCE);
        if (answer.success) {
          const granted: Partial<IStateUsePaymentBalancePoll> = { creditsGranted: answer.value.matchCredits };
          this.state.update(granted);
          return;
        }
        // refused or unreachable — try again, a few times
        attempts++;
        if (attempts < MAX_ATTEMPTS) retryTimer = this.timers.setTimeout(() => { void fetchBalance(); }, RETRY_DELAY_MS);
      };
      retryTimer = this.timers.setTimeout(() => { void fetchBalance(); }, INITIAL_DELAY_MS);
      return () => this.timers.clearTimeout(retryTimer);
    },
  }];
  private readonly reactions = new Reactions<TPollInputs>(this.declared);

  constructor(
    private readonly useCases: Pick<IPortClientUseCases, 'readBalance'>,
    private readonly requests: ClientRequests,
    private readonly timers: IServiceViewTimers,
  ) {}

  /** Its reaction over the facts of the screen's model that composes it (the payment's success screen, P5). */
  reactionsOver<TFacts>(factsOf: (facts: TFacts) => TPollInputs): Array<IConfigReaction<TFacts>> {
    return Reactions.over(this.declared, factsOf);
  }

  getSnapshot(): IStateUsePaymentBalancePoll {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(inputs: readonly unknown[]): void {
    this.reactions.stopChanged(inputs as TPollInputs);
  }

  commit(inputs: readonly unknown[]): void {
    this.reactions.startChanged(inputs as TPollInputs);
  }
}
